import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ErrorMessage } from '@/components/ui/error-message';
import { useAuthStore } from '@/store/authStore';
import Routes from '@/router/routes';
import TelletLogo from '@/assets/logo/tellet-logo-main.svg';
import { sixDigitCodeSchema } from '@/lib/validationSchemas';

const backupCodeSchema = Yup.string()
  .matches(/^[A-Z0-9]{5}-[A-Z0-9]{5}$/, 'Backup code must be in format XXXXX-XXXXX')
  .required('Code is required');

// Use Yup.lazy so the resolver always evaluates the current schema
const dynamicSchema = (isBackupRef: React.MutableRefObject<boolean>) =>
  Yup.object().shape({
    code: Yup.lazy(() => (isBackupRef.current ? backupCodeSchema : sixDigitCodeSchema)),
  });

type FormData = { code: string };

const defaultValues: FormData = { code: '' };

export default function TwoFactorVerify() {
  const navigate = useNavigate();
  const { verify2FA, isLoading, error, clearError, requires2FA, tempToken } = useAuthStore();
  const [useBackupCode, setUseBackupCode] = useState(false);
  const useBackupCodeRef = useRef(useBackupCode);

  const {
    setValue,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(dynamicSchema(useBackupCodeRef)),
    defaultValues,
  });

  const code = watch('code');

  // Redirect if not in 2FA flow — must be in useEffect to avoid setState during render
  const shouldRedirect = !requires2FA && !tempToken;
  useEffect(() => {
    if (shouldRedirect) {
      navigate(Routes.login.path);
    }
  }, [shouldRedirect, navigate]);

  if (shouldRedirect) {
    return null;
  }

  const onFormSubmit = async (data: FormData) => {
    clearError();

    try {
      await verify2FA(data.code);
      navigate(Routes.super_admin_dashboard.path);
    } catch (err) {
      // Error handled in store
    }
  };

  const toggleBackupMode = () => {
    const newValue = !useBackupCode;
    useBackupCodeRef.current = newValue;
    setUseBackupCode(newValue);
    reset(defaultValues);
    clearError();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src={TelletLogo} alt="Tellet" className="mx-auto h-12 w-auto dark:invert" />
          <h2 className="mt-6 text-2xl font-bold text-foreground">Two-Factor Authentication</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {useBackupCode
              ? 'Enter one of your backup codes'
              : 'Enter the 6-digit code from your authenticator app'}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onFormSubmit)}>
          <div>
            {useBackupCode ? (
              <>
                <Input
                  id="backup-code"
                  type="text"
                  placeholder="XXXXX-XXXXX"
                  value={code}
                  onChange={(e) =>
                    setValue('code', e.target.value.toUpperCase(), { shouldValidate: false })
                  }
                  className="text-center text-xl tracking-widest"
                  hasError={!!errors.code || !!error}
                />
                <ErrorMessage message={errors.code?.message} />
              </>
            ) : (
              <>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) =>
                    setValue('code', e.target.value.replace(/\D/g, ''), { shouldValidate: false })
                  }
                  className="text-center text-2xl tracking-widest"
                  hasError={!!errors.code || !!error}
                />
                <ErrorMessage message={errors.code?.message} />
              </>
            )}
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading || (useBackupCode ? code.length < 11 : code.length !== 6)}
          >
            {isLoading ? 'Verifying...' : 'Verify'}
          </Button>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-background text-muted-foreground">or</span>
          </div>
        </div>

        <button
          type="button"
          onClick={toggleBackupMode}
          className="w-full text-center text-sm text-primary hover:text-primary/80 underline"
        >
          {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code instead'}
        </button>
      </div>
    </div>
  );
}
